<template>
  <v-scale-transition>
    <panel
      :breadcrumbs="breadcrumbs"
      :title="title"
    >
      <v-toolbar flat>
        <v-toolbar-title>SIM Pack</v-toolbar-title>
        <v-divider
          class="mx-2"
          inset
          vertical
        />
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
        <v-btn
          v-if="selected.length"
          color="error"
          dark
          class="ml-2"
          @click="bulkDeleteDialog = true"
        >
          Delete Selected ({{ selected.length }})
        </v-btn>
        <v-dialog
          v-model="bulkDeleteDialog"
          max-width="500px"
        >
          <v-card>
            <v-card-title>
              <span class="headline">Delete SIM Packs</span>
            </v-card-title>
            <v-card-text>
              <v-alert
                :value="true"
                type="warning"
              >
                This action is irreversible!
                Are you sure you want to delete <strong>{{ selected.length }}</strong> SIM pack(s)?
              </v-alert>
              <v-alert
                v-model="bulkDeleteAlert"
                type="error"
              >
                {{ bulkDeleteAlertMessage }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue darken-1"
                flat
                :disabled="bulkDeleting"
                @click="bulkDeleteDialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="red darken-1"
                flat
                :loading="bulkDeleting"
                :disabled="bulkDeleting"
                @click="bulkDeleteSimpacks"
              >
                Delete Selected
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="deleteSimcardDialog"
          max-width="500px"
        >
          <v-card>
            <v-card-title>
              <span class="headline">Delete SIM Pack</span>
            </v-card-title>
            <v-card-text>
              <v-alert
                :value="true"
                type="warning"
              >
                This action is irreversible!
                Are you sure you want to delete SIM pack <strong>{{ itemToDelete && itemToDelete.iccid }}</strong>?
              </v-alert>
              <v-alert
                v-model="deleteAlert"
                type="error"
              >
                {{ deleteAlertMessage }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue darken-1"
                flat
                @click="closeDeleteDialog"
              >
                Cancel
              </v-btn>
              <v-btn
                color="red darken-1"
                flat
                @click="deleteSimpack"
              >
                Delete SIM Pack
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="bulkDialog"
          max-width="500px"
        >
          <template #activator="{ on }">
            <v-btn
              color="primary"
              dark
              class="ml-2 mb-2"
              v-on="on"
            >
              Bulk Upload SIM Packs
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="headline">Bulk Upload SIM Packs</span>
            </v-card-title>

            <v-card-actions>
              <v-btn-toggle
                v-model="bulkCategory"
                mandatory
                class="elevation-0"
              >
                <v-btn
                  flat
                  value="upload"
                >
                  <v-icon left>
                    mdi-sim
                  </v-icon>
                  Upload Sim
                </v-btn>
                <v-btn
                  flat
                  value="blank"
                >
                  <v-icon left>
                    mdi-sim-outline
                  </v-icon>
                  Blank Sim
                </v-btn>
                <v-btn
                  v-if="showBulkMnpSim"
                  flat
                  value="mnp"
                >
                  <v-icon left>
                    mdi-swap-horizontal
                  </v-icon>
                  MNP Sim
                </v-btn>
              </v-btn-toggle>
            </v-card-actions>

            <v-card-text>
              <v-alert
                :value="true"
                type="info"
              >
                <span v-if="bulkCategory === 'upload'">
                  Upload an Excel/CSV file with columns <strong>Iccid</strong> and <strong>Msisdn</strong>. Plan Type is automatically set to Prepaid.
                </span>
                <span v-else-if="bulkCategory === 'blank'">
                  Upload an Excel/CSV file with only an <strong>Iccid</strong> column. Msisdn can be filled in the table after uploading. Plan Type is automatically set to Prepaid.
                </span>
                <span v-else>
                  Upload an Excel/CSV file with only an <strong>Iccid</strong> column for MNP SIM packs. Msisdn is not required here — it will be assigned later during port-in. Plan Type is automatically set to MNP.
                </span>
              </v-alert>

              <v-btn
                color="grey darken-1"
                outline
                class="mb-2"
                @click="downloadBulkTemplate"
              >
                <v-icon
                  left
                  small
                >
                  mdi-download
                </v-icon>
                Download Template
              </v-btn>
              <br>

              <input
                ref="bulkFileInput"
                type="file"
                accept=".xlsx,.xls,.csv"
                style="display: none"
                @change="handleBulkFileChange"
              >
              <v-btn
                color="blue darken-1"
                outline
                @click="triggerBulkFileInput"
              >
                Choose File
              </v-btn>
              <span
                v-if="bulkFile"
                class="ml-2"
              >{{ bulkFile.name }}</span>

              <v-alert
                v-model="bulkAlert"
                type="error"
                class="mt-3"
              >
                {{ bulkAlertMessage }}
              </v-alert>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue darken-1"
                flat
                @click="bulkDialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue darken-1"
                flat
                :disabled="!bulkFile"
                @click="parseBulkFile"
              >
                Parse File
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="uploadDialog"
          max-width="500px"
        >
          <template #activator="{ on }">
            <v-btn
              color="primary"
              dark
              class="ml-4 mb-2"
              v-on="on"
            >
              Upload SIM Pack
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>

            <v-card-actions>
              <v-btn-toggle
                v-model="simCategory"
                mandatory
                class="elevation-0"
              >
                <v-btn
                  flat
                  value="upload"
                  :disabled="uploading"
                >
                  <v-icon left>
                    mdi-sim
                  </v-icon>
                  Upload Sim
                </v-btn>
                <v-btn
                  flat
                  value="blank"
                  :disabled="uploading"
                >
                  <v-icon left>
                    mdi-sim-outline
                  </v-icon>
                  Blank Sim
                </v-btn>
                <v-btn
                  v-if="showMnpSim"
                  flat
                  value="mnp"
                  :disabled="uploading"
                >
                  <v-icon left>
                    mdi-swap-horizontal
                  </v-icon>
                  MNP Sim
                </v-btn>
              </v-btn-toggle>
            </v-card-actions>

            <v-card-text>
              <v-alert
                :value="true"
                type="info"
              >
                Please ensure that you <span style="text-decoration: underline;"><strong>collect the payment</strong></span> from the subscriber first before uploading the simpack!
              </v-alert>
              <v-form
                ref="uploadForm"
                v-model="validSimpack"
                lazy-validation
              >
                <v-container grid-list-md>
                  <v-layout wrap>
                    <v-flex xs12>
                      <v-text-field
                        v-model="newSimpack.iccid"
                        label="Iccid"
                        counter="20"
                        :rules="[val => !!val || 'Iccid required!']"
                        validate-on-blur
                      >
                        <v-icon
                          slot="append"
                          :color="iccidLooksValid ? 'success' : 'grey lighten-1'"
                        >
                          {{ iccidLooksValid ? 'mdi-check-circle' : 'mdi-credit-card-outline' }}
                        </v-icon>
                      </v-text-field>
                    </v-flex>
                    <v-flex
                      xs12
                      v-if="simCategory === 'upload'"
                    >
                      <v-text-field
                        v-model="newSimpack.msisdn"
                        label="Msisdn"
                        :rules="[val => !!val || 'Msisdn required!']"
                        validate-on-blur
                      />
                    </v-flex>
                    <v-flex
                      xs12
                      v-else-if="simCategory === 'blank'"
                    >
                      <v-autocomplete
                        v-model="newSimpack.msisdn"
                        :items="msisdnList"
                        :search-input.sync="search2"
                        label="Msisdn (2 digits)"
                        item-text="msisdn"
                        item-value="msisdn"
                        validate-on-blur
                        @update:search-input="onSearchInput"
                        @focus="onFocus"
                        :rules="[val => !!val || 'Msisdn required!']"
                      />
                    </v-flex>
                    <v-flex xs12>
                      <v-select
                        v-model="newSimpack.plan"
                        label="Plan"
                        :items="packages"
                        :rules="[val => !!val || 'Plan required!']"
                        item-text="name"
                        item-value="code"
                        validate-on-blur
                      >
                        <template
                          slot="item"
                          slot-scope="data"
                        >
                          <v-list-tile-content>
                            <v-list-tile-title v-html="data.item.code" />
                            <v-list-tile-sub-title v-html="data.item.description" />
                          </v-list-tile-content>
                        </template>
                      </v-select>
                      <v-slide-y-transition>
                        <v-alert
                          v-if="selectedPlanDetail"
                          :value="true"
                          type="success"
                          outline
                          class="mt-1 py-2"
                        >
                          <strong>{{ selectedPlanDetail.code }}</strong> — RM{{ selectedPlanDetail.price }}
                          <div
                            v-if="selectedPlanDetail.description"
                            class="caption"
                            v-html="selectedPlanDetail.description"
                          />
                        </v-alert>
                      </v-slide-y-transition>
                    </v-flex>
                    <!-- Data plan is only offered for MNP Sim -->
                    <template v-if="simCategory === 'mnp' && dataPlanOptions.length">
                      <v-flex xs12>
                        <v-select
                          v-model="newSimpack.dataPlan"
                          label="Data Plan (optional)"
                          :items="dataPlanOptions"
                          item-text="name"
                          item-value="code"
                          clearable
                        >
                          <template
                            slot="item"
                            slot-scope="data"
                          >
                            <v-list-tile-content>
                              <v-list-tile-title>{{ data.item.name }} — RM{{ data.item.price }}</v-list-tile-title>
                              <v-list-tile-sub-title>{{ data.item.description }} ({{ data.item.planValidity }})</v-list-tile-sub-title>
                            </v-list-tile-content>
                          </template>
                        </v-select>
                        <v-slide-y-transition>
                          <v-alert
                            v-if="selectedDataPlanDetail"
                            :value="true"
                            type="success"
                            outline
                            class="mt-1 py-2"
                          >
                            <strong>{{ selectedDataPlanDetail.name }}</strong> — RM{{ selectedDataPlanDetail.price }}
                            <div class="caption">
                              {{ selectedDataPlanDetail.description }} ({{ selectedDataPlanDetail.planValidity }})
                            </div>
                          </v-alert>
                        </v-slide-y-transition>
                      </v-flex>
                    </template>
                  </v-layout>
                </v-container>
              </v-form>
            </v-card-text>

            <v-alert
              v-model="alert"
              type="error"
            >
              {{ alertMessage }}
            </v-alert>
            <v-card-actions>
              <v-btn
                color="blue darken-1"
                flat
                :disabled="uploading"
                @click="listMsisdns()"
              >
                Refresh
              </v-btn>
              <v-spacer />
              <v-btn
                color="blue darken-1"
                flat
                :disabled="uploading"
                @click="closeAllDialogs(false)"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue darken-1"
                flat
                :loading="uploading"
                :disabled="uploading"
                @click="submitUpload"
              >
                {{ uploadButtonLabel }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
      <v-alert
        :value="true"
        type="info"
      >
        Only <strong>Prepaid</strong> SIM packs can be bulk uploaded or withdrawn here.
      </v-alert>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="items"
        :loading="loading"
        :search="search"
        item-key="iccid"
        class="elevation-1"
        disable-initial-sort
      >
        <template #items="props">
          <tr>
            <td class="text-xs-center">
              <v-checkbox
                v-if="canDeleteSimpack(props.item)"
                :input-value="props.selected"
                :disabled="!props.selected && selected.length >= maxBulkDelete"
                hide-details
                @change="props.selected = $event"
              />
            </td>
            <td class="text-xs-left">
              {{ props.item.entry }}
            </td>
            <td class="text-xs-left">
              {{ props.item.iccid }}
            </td>
            <td class="text-xs-left">
              {{ props.item.msisdn }}
            </td>
            <td class="text-xs-left">
              {{ props.item.plan }}
            </td>
            <td class="text-xs-left">
              {{ props.item.dataPlan || '-' }}
            </td>
            <td class="text-xs-left">
              {{ props.item.activation }}
            </td>
            <td class="text-xs-left">
              <v-chip
                small
                :color="statusColor(props.item.status)"
                text-color="white"
              >
                {{ props.item.status }}
              </v-chip>
            </td>
            <td class="text-xs-left">
              {{ props.item.remark }}
            </td>
            <td class="text-xs-center">
              <v-icon
                v-if="canDeleteSimpack(props.item)"
                small
                color="red lighten-2"
                @click="confirmDeleteSimpack(props.item)"
              >
                mdi-trash-can-outline
              </v-icon>
            </td>
          </tr>
        </template>
        <template #no-data>
          <v-btn
            color="primary"
            @click="getDealerSim"
          >
            Load dealer SIM packs
          </v-btn>
        </template>
      </v-data-table>

      <v-card class="mt-4">
        <v-toolbar
          flat
          color="grey darken-3"
          dark
        >
          <v-toolbar-title>
            Bulk Upload SIM Packs
            <v-chip
              small
              class="ml-2"
            >
              {{ bulkTableData.length }} row(s)
            </v-chip>
          </v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="bulkSearch"
            append-icon="mdi-magnify"
            label="Search"
            dark
            single-line
            hide-details
            class="mr-2"
          />
          <v-btn
            flat
            class="ml-2"
            :disabled="!bulkTableData.length"
            @click="clearBulkTable"
          >
            Clear
          </v-btn>
          <v-btn
            color="primary"
            class="ml-2"
            :loading="bulkSubmitting"
            :disabled="bulkSubmitting || !bulkTableData.length"
            @click="submitBulkUpload"
          >
            Submit Bulk Upload
          </v-btn>
        </v-toolbar>

        <v-alert
          v-model="bulkParseAlert"
          :type="bulkParseAlertType"
        >
          {{ bulkParseAlertMessage }}
        </v-alert>

        <v-data-table
          :headers="bulkHeaders"
          :items="bulkTableData"
          :search="bulkSearch"
          class="elevation-1"
          disable-initial-sort
          hide-actions
        >
          <template #items="props">
            <tr :class="{ 'grey darken-2': !isBulkRowValid(props.item) }">
              <td class="text-xs-left">
                <v-text-field
                  v-model="props.item.iccid"
                  hide-details
                  single-line
                  placeholder="Iccid"
                />
              </td>
              <td class="text-xs-left">
                <v-text-field
                  v-if="bulkCategory === 'upload'"
                  v-model="props.item.msisdn"
                  hide-details
                  single-line
                  placeholder="Msisdn"
                />
                <v-autocomplete
                  v-else-if="bulkCategory === 'blank'"
                  v-model="props.item.msisdn"
                  :items="msisdnList"
                  item-text="msisdn"
                  item-value="msisdn"
                  hide-details
                  single-line
                  placeholder="Select Msisdn"
                  @focus="onFocus"
                />
                <span
                  v-else
                  class="grey--text"
                >Assigned during port-in</span>
              </td>
              <td class="text-xs-left">
                {{ props.item.plan }}
              </td>
              <td class="text-xs-left">
                <v-chip
                  v-if="props.item.status"
                  small
                  :color="statusColor(props.item.status)"
                  text-color="white"
                >
                  {{ props.item.status }}
                </v-chip>
                <span
                  v-if="props.item.status === 'Error' && props.item.remark"
                  class="grey--text"
                >({{ props.item.remark }})</span>
              </td>
              <td class="text-xs-center">
                <v-icon
                  small
                  color="red lighten-2"
                  @click="removeBulkRow(props.item)"
                >
                  mdi-close-circle-outline
                </v-icon>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <v-snackbar
        v-model="snackbar"
        color="success"
        :timeout="5000"
      >
        {{ snackbarText }}
        <v-btn
          dark
          flat
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>
      <v-snackbar
        v-model="snackbarError"
        color="error"
        :timeout="5000"
      >
        {{ snackbarText }}
        <v-btn
          dark
          flat
          @click="snackbarError = false"
        >
          Close
        </v-btn>
      </v-snackbar>
    </panel>
  </v-scale-transition>
</template>

<script>
import * as XLSX from 'xlsx'
import { createGetParams, createPostParams } from '@/rest'
import security from '@/security'

const pageTitle = 'Upload SIM Pack'
const DEFAULT_BULK_PLAN_CODE = 'Prepaid3 Offer'
const MNP_PLAN_CODE = 'Prepaid3 Offer'
// MNP Sim is hidden for everyone except these dealers
const MNP_SIM_DEALERS = ['DYSD0102', '01113224803']
// Bulk MNP Sim is hidden for everyone; set to true to show it again
const SHOW_BULK_MNP_SIM = false
const MAX_BULK_ROWS = 100
const MAX_BULK_DELETE = 30

export default {
  components: {
    Panel: () => import(/* webpackChunkName: "widePanel" */ '@/components/WidePanel'),
  },
  data () {
    return {
      simCategory: 'upload',
      showMnpSim: MNP_SIM_DEALERS.includes(security.me.dealerId()),
      showBulkMnpSim: SHOW_BULK_MNP_SIM,
      title: pageTitle,
      items: [],
      msisdn: null,
      msisdnList: [],
      msisdnLoading: false,
      packages: [],
      loading: false,
      uploading: false,
      uploadDialog: false,
      bulkDialog: false,
      bulkCategory: 'upload',
      bulkFile: null,
      bulkTableData: [],
      bulkSearch: null,
      bulkSubmitting: false,
      bulkAlert: false,
      bulkAlertMessage: null,
      bulkParseAlert: false,
      bulkParseAlertType: 'warning',
      bulkParseAlertMessage: null,
      bulkHeaders: [
        {
          text: 'Iccid',
          align: 'left',
          sortable: false,
          value: 'iccid',
        },
        {
          text: 'Msisdn',
          align: 'left',
          sortable: false,
          value: 'msisdn',
        },
        {
          text: 'Plan Type',
          align: 'left',
          sortable: false,
          value: 'plan',
        },
        {
          text: 'Status',
          align: 'left',
          sortable: false,
          value: 'status',
        },
        {
          text: '',
          align: 'center',
          sortable: false,
          value: 'remove',
        },
      ],
      breadcrumbs: [
        {
          text: 'Dealer Kits', disabled: false, to: '/dealer_kits',
        },
        {
          text: pageTitle, disabled: true,
        },
      ],
      headers: [
        {
          text: '',
          align: 'center',
          sortable: false,
          value: 'select',
        },
        {
          text: 'Entry',
          align: 'left',
          sortable: true,
          value: 'entry',
        },
        {
          text: 'Iccid',
          align: 'left',
          sortable: true,
          value: 'iccid',
        },
        {
          text: 'Msisdn',
          align: 'left',
          sortable: true,
          value: 'msisdn',
        },
        {
          text: 'Plan',
          align: 'left',
          sortable: true,
          value: 'plan',
        },
        {
          text: 'Data Plan',
          align: 'left',
          sortable: true,
          value: 'dataPlan',
        },
        {
          text: 'Activation',
          align: 'left',
          sortable: true,
          value: 'activation',
        },
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'status',
        },
        {
          text: 'Remark',
          align: 'left',
          sortable: true,
          value: 'remark',
        },
        {
          text: 'Action',
          align: 'center',
          sortable: false,
          value: 'action',
        },
      ],
      newSimpack: {
        iccid: null,
        msisdn: null,
        plan: null,
        planInfo: null,
        dataPlan: null,
        amount: '0.00',
        // emoneyId: null,
      },
      defaultSimpack: {
        iccid: null,
        msisdn: null,
        plan: null,
        planInfo: null,
        dataPlan: null,
        amount: '0.00',
        // emoneyId: null,
      },
      formTitle: 'Upload SIM Pack',
      deleteSimcardDialog: false,
      itemToDelete: null,
      deleteAlert: false,
      deleteAlertMessage: null,
      selected: [],
      maxBulkDelete: MAX_BULK_DELETE,
      bulkDeleteDialog: false,
      bulkDeleting: false,
      bulkDeleteAlert: false,
      bulkDeleteAlertMessage: null,
      alert: false,
      alertMessage: null,
      snackbar: false,
      snackbarError: false,
      snackbarText: null,
      validSimpack: true,
      search: null,
      search2: null,
      search2Id: null,
    }
  },
  computed: {
    filteredMsisdnList () {
      const search = (this.search2 || '').trim()
      if (search.length < 2) return []

      return this.msisdnList.filter(item =>
        item.msisdn.includes(search),
      )
    },
    uploadButtonLabel () {
      if (this.simCategory === 'blank') return 'Upload Blank Sim'
      if (this.simCategory === 'mnp') return 'Upload MNP Sim'
      return 'Upload Simpack'
    },
    iccidLooksValid () {
      const iccid = (this.newSimpack.iccid || '').replace(/\s/g, '')
      return iccid.length >= 15 && /^\d+$/.test(iccid)
    },
    selectedPlanDetail () {
      return this.packages.find(plan => plan.code === this.newSimpack.plan) || null
    },
    // Prepaid3 plans (ONE-X, SISWA, ...) returned by the backend on the prepaid package
    prepaidPlans () {
      return (this.selectedPlanDetail && this.selectedPlanDetail.plans) || []
    },
    dataPlanOptions () {
      const plan = this.prepaidPlans.find(p => p.plan === this.newSimpack.planInfo)
      return (plan && plan.dataPlans) || []
    },
    selectedDataPlanDetail () {
      return this.dataPlanOptions.find(d => d.code === this.newSimpack.dataPlan) || null
    },
  },
  watch: {
    uploadDialog (val) {
      if (val) {
        this.$refs.uploadForm.resetValidation()
      } else {
        this.closeAllDialogs(false)
      }
    },
    simCategory () {
      this.newSimpack.msisdn = null
      this.newSimpack.plan = null
      this.newSimpack.planInfo = null
      this.newSimpack.dataPlan = null
    },
    // Prepaid plan is not chosen by the dealer; it always defaults to ONE-X
    'newSimpack.plan' () {
      const oneX = this.prepaidPlans.find(p => /^one-?x$/i.test((p.plan || '').trim()))
      this.newSimpack.planInfo = oneX ? oneX.plan : null
      this.newSimpack.dataPlan = null
    },
    'newSimpack.planInfo' () {
      this.newSimpack.dataPlan = null
    },
    bulkDialog (val) {
      if (val) {
        this.bulkFile = null
        this.bulkAlert = false
      }
    },
    search2: function (val) {
      clearTimeout(this.searchId2)
      this.cancelRequest()

      // If empty input (user clicked but didn't type)
      if (val === '' || val === null) {
        this.listMsisdns() // get full 100 numbers
        return
      }

      // If input less than 4, clear list (no search)
      if (val.length < 2) {
        this.msisdnList = [] // empty list, no suggestions
        return
      }

      // If input 4 or more, debounce and search
      this.searchId2 = setTimeout(() => {
        this.listMsisdns()
      }, 1000)
    },

    deleteSimcardDialog (val) {
      if (!val) {
        this.deleteAlert = false
        this.itemToDelete = null
      }
    },

    bulkDeleteDialog (val) {
      if (!val) {
        this.bulkDeleteAlert = false
        this.bulkDeleteAlertMessage = null
      }
    },
  },

  created: function () {
    this.getDealerSim()
    this.getDealerSimPackages()
  },

  methods: {
    onFocus () {
      // Only fetch once: refetching on every row's focus would overwrite the
      // shared msisdnList and drop numbers already picked in other rows.
      if (!this.search2 && !this.msisdnList.length) {
        this.listMsisdns()
      }
    },
    getDealerSimPackages () {
      const params = createGetParams({})
      this.loading = true
      this.$rest.get('getDealerSimPackages.php', params)
        .then((response) => {
          this.packages = response.data
          this.loading = false
        }).catch(error => {
          console.log(error)
          this.loading = false
        })
    },

    cancelRequest: function () {
      this.msisdnLoading = false
      if (this.source) {
        this.source.cancel('Forced cancellation.')
        this.source = null
      }
    },

    getDealerSim () {
      const params = createGetParams({})
      this.loading = true
      this.$rest.get('getDealerSim.php', params)
        .then((response) => {
          this.items = response.data
          this.loading = false
        }).catch(error => {
          console.log(error)
          this.loading = false
        })
    },

    onSearchInput (val) {
      if (/^\d{2}$/.test(val)) {
        this.filteredMsisdnList = this.allMsisdns.filter(msisdnObj =>
          msisdnObj.msisdn.startsWith(val),
        )
      } else {
        this.filteredMsisdnList = []
      }
    },

    listMsisdns: async function () {
      this.msisdnLoading = true

      const param = createGetParams({ search: this.search2 })

      try {
        this.source = this.$rest.CancelToken.source()
        Object.assign(param, { cancelToken: this.source.token })
        const response = await this.$rest.get('listMsisdns.php', param)
        this.msisdnList = response.data
      } catch (error) {
        if (!this.$rest.isCancel(error)) {
          this.hint = error.message
        }
        this.msisdnList = []
      } finally {
        this.msisdnLoading = false
      }
    },

    statusColor (status) {
      switch (status) {
        case 'Activated':
        case 'Success':
          return 'success'
        case 'Expired':
          return 'warning'
        case 'Pending':
          return 'grey'
        case 'Error':
          return 'error'
        default:
          return 'error'
      }
    },

    canDeleteSimpack (item) {
      if (item.status !== 'Pending') return false
      const unpaid = item.emoneyId === '0' && !item.transactionId
      // Pending SIM packs bought with a data plan are already paid, but can still be withdrawn
      const paidWithDataPlan = !!item.dataPlan && !!item.emoneyId && item.emoneyId !== '0' && !!item.transactionId
      return unpaid || paidWithDataPlan
    },

    confirmDeleteSimpack (item) {
      this.itemToDelete = item
      this.deleteSimcardDialog = true
    },

    closeDeleteDialog () {
      this.deleteSimcardDialog = false
    },

    async deleteSimpack () {
      if (!this.itemToDelete) return

      try {
        const params = createPostParams({
          action: 'deleteSimpack',
          payload: {
            iccid: this.itemToDelete.iccid,
            msisdn: this.itemToDelete.msisdn,
            emoneyId: this.itemToDelete.emoneyId,
            transactionId: this.itemToDelete.transactionId,
          },
        })
        await this.$rest.post('postAction.php', params)

        this.snackbarText = 'You have successfully deleted the SIM pack.'
        this.snackbar = true
        this.closeDeleteDialog()
        this.getDealerSim()
      } catch (error) {
        let message = error.message
        if (error.response) {
          message += ': ' + error.response.data
        }
        this.deleteAlertMessage = message
        this.deleteAlert = true
      }
    },

    async bulkDeleteSimpacks () {
      if (!this.selected.length) return

      this.bulkDeleting = true
      this.bulkDeleteAlert = false

      const targets = this.selected.slice(0, this.maxBulkDelete)
      const results = await Promise.allSettled(targets.map(item => {
        const params = createPostParams({
          action: 'deleteSimpack',
          payload: {
            iccid: item.iccid,
            msisdn: item.msisdn,
            emoneyId: item.emoneyId,
            transactionId: item.transactionId,
          },
        })
        return this.$rest.post('postAction.php', params)
      }))

      const failedCount = results.filter(result => result.status === 'rejected').length
      this.bulkDeleting = false

      if (failedCount) {
        this.bulkDeleteAlertMessage = `Failed to delete ${failedCount} of ${targets.length} SIM pack(s). Please try again.`
        this.bulkDeleteAlert = true
      } else {
        this.bulkDeleteDialog = false
      }

      this.snackbarText = `Deleted ${targets.length - failedCount} of ${targets.length} SIM pack(s).`
      this.snackbar = true
      this.selected = []
      this.getDealerSim()
    },

    closeAllDialogs (save) {
      this.uploadDialog = false
      this.alert = false
      setTimeout(() => {
        this.newSimpack = Object.assign({}, this.defaultSimpack)
        save && this.getDealerSim()
      }, 300)
    },

    async uploadSimpack () {
      if (!this.$refs.uploadForm.validate()) {
        return
      }

      const packageCode = this.newSimpack.plan
      const plan = this.packages.find(plan =>
        plan.code === packageCode,
      )
      this.newSimpack.amount = plan.price

      this.uploading = true
      try {
        const params = createPostParams({
          action: 'uploadDealerSim',
          payload: this.newSimpack,
        })
        await this.$rest.post('postAction.php', params)

        this.snackbarText = 'You have successfully uploaded a SIM pack.'
        this.snackbar = true
        this.closeAllDialogs(true)
      } catch (error) {
        let message = error.message
        if (error.response) {
          message += ': ' + error.response.data
        }
        this.showError(message)
      } finally {
        this.uploading = false
      }
    },

    async uploadBlankSim () {
      if (!this.$refs.uploadForm.validate()) {
        return
      }

      const packageCode = this.newSimpack.plan
      const plan = this.packages.find(plan =>
        plan.code === packageCode,
      )
      this.newSimpack.amount = plan.price

      this.uploading = true
      try {
        const params = createPostParams({
          action: 'uploadBlankSim',
          payload: this.newSimpack,
        })
        await this.$rest.post('postAction.php', params)

        this.snackbarText = 'You have successfully uploaded a Blank SIM.'
        this.snackbar = true
        this.closeAllDialogs(true)
      } catch (error) {
        let message = error.message
        if (error.response) {
          message += ': ' + error.response.data
        }
        this.showError(message)
      } finally {
        this.uploading = false
      }
    },

    submitUpload () {
      if (this.simCategory === 'blank') {
        this.uploadBlankSim()
      } else if (this.simCategory === 'mnp') {
        this.uploadMnpSim()
      } else {
        this.uploadSimpack()
      }
    },

    async uploadMnpSim () {
      if (!this.$refs.uploadForm.validate()) {
        return
      }

      const packageCode = this.newSimpack.plan
      const plan = this.packages.find(plan =>
        plan.code === packageCode,
      )
      this.newSimpack.amount = plan.price

      this.uploading = true
      try {
        const params = createPostParams({
          action: 'uploadMnpSim',
          payload: this.newSimpack,
        })
        await this.$rest.post('postAction.php', params)

        this.snackbarText = 'You have successfully uploaded an MNP SIM.'
        this.snackbar = true
        this.closeAllDialogs(true)
      } catch (error) {
        let message = error.message
        if (error.response) {
          message += ': ' + error.response.data
        }
        this.showError(message)
      } finally {
        this.uploading = false
      }
    },

    showError (message) {
      this.alertMessage = message
      this.alert = true
    },

    showBulkError (message) {
      this.snackbarText = message
      this.snackbarError = true
    },

    triggerBulkFileInput () {
      this.$refs.bulkFileInput.click()
    },

    handleBulkFileChange (event) {
      this.bulkFile = event.target.files[0] || null
    },

    downloadBulkTemplate () {
      const headerRow = this.bulkCategory === 'upload' ? ['Iccid', 'Msisdn'] : ['Iccid']
      const worksheet = XLSX.utils.aoa_to_sheet([headerRow])
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template')

      const filenames = {
        upload: 'upload_sim_template.csv',
        blank: 'blank_sim_template.csv',
        mnp: 'mnp_sim_template.csv',
      }
      XLSX.writeFile(workbook, filenames[this.bulkCategory], { bookType: 'csv' })
    },

    parseBulkFile () {
      if (!this.bulkFile) return

      this.bulkAlert = false
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array', raw: true })
          const sheet = workbook.Sheets[workbook.SheetNames[0]]
          const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })
          this.processBulkData(json)
          this.bulkDialog = false
        } catch (error) {
          this.bulkAlertMessage = 'Failed to read file: ' + error.message
          this.bulkAlert = true
        }
      }
      reader.readAsArrayBuffer(this.bulkFile)
    },

    processBulkData (rows) {
      const needsMsisdn = this.bulkCategory === 'upload'
      const planCode = this.bulkCategory === 'mnp' ? MNP_PLAN_CODE : DEFAULT_BULK_PLAN_CODE

      const parsed = rows
        .map(row => {
          const normalized = {}
          Object.keys(row).forEach(key => {
            normalized[key.toString().trim().toLowerCase()] = row[key]
          })
          return {
            iccid: (normalized.iccid || '').toString().trim(),
            msisdn: needsMsisdn ? (normalized.msisdn || '').toString().trim() : '',
            plan: planCode,
            status: null,
            remark: null,
          }
        })
        .filter(row => row.iccid)

      const availableSlots = MAX_BULK_ROWS - this.bulkTableData.length
      if (availableSlots <= 0) {
        this.bulkParseAlertType = 'error'
        this.bulkParseAlertMessage = `You can only upload a maximum of ${MAX_BULK_ROWS} SIM packs at a time. Please submit or clear the current batch before adding more.`
        this.bulkParseAlert = true
        return
      }

      const truncated = parsed.length > availableSlots
      const accepted = parsed.slice(0, availableSlots)

      this.bulkTableData = this.bulkTableData.concat(accepted)

      if (truncated) {
        this.bulkParseAlertType = 'warning'
        this.bulkParseAlertMessage = `Only ${accepted.length} of ${parsed.length} row(s) were added. ` +
          `You can only upload a maximum of ${MAX_BULK_ROWS} SIM packs at a time.`
        this.bulkParseAlert = true
      }

      // Catches Iccids that were already stored as a lossy number in the source
      // spreadsheet (e.g. "8.96019E+17") before it ever reached our parser.
      const corrupted = accepted.filter(row => /e\+?\d+$/i.test(row.iccid))
      if (corrupted.length) {
        this.bulkParseAlertType = 'warning'
        this.bulkParseAlertMessage = `${corrupted.length} row(s) have an Iccid that lost precision in the source file ` +
          '(shows as scientific notation, e.g. "8.96019E+17"). In Excel, format the Iccid column as Text, ' +
          're-enter the full number, then re-upload.'
        this.bulkParseAlert = true
      }

      if (this.bulkCategory === 'blank') {
        this.listMsisdns()
      }
    },

    isBulkRowValid (row) {
      const needsMsisdn = this.bulkCategory !== 'mnp'
      return !!(row.iccid && row.plan && (!needsMsisdn || row.msisdn))
    },

    removeBulkRow (row) {
      this.bulkTableData = this.bulkTableData.filter(item => item !== row)
    },

    clearBulkTable () {
      this.bulkTableData = []
      this.bulkParseAlert = false
    },

    async submitBulkUpload () {
      if (!this.bulkTableData.length) return

      if (this.bulkTableData.length > MAX_BULK_ROWS) {
        this.showBulkError(`You can only upload a maximum of ${MAX_BULK_ROWS} SIM packs at a time. Please remove ${this.bulkTableData.length - MAX_BULK_ROWS} row(s) before submitting.`)
        return
      }

      const invalidCount = this.bulkTableData.filter(row => !this.isBulkRowValid(row)).length
      if (invalidCount) {
        this.showBulkError(`${invalidCount} row(s) are missing Iccid, Msisdn or Plan Type. Please complete them before submitting.`)
        return
      }

      this.bulkSubmitting = true
      this.bulkParseAlert = false

      const payload = this.bulkTableData.map(row => {
        const plan = this.packages.find(p => p.code === row.plan)
        return {
          iccid: row.iccid,
          msisdn: row.msisdn,
          plan: row.plan,
          amount: plan ? plan.price : '0.00',
        }
      })

      const bulkActions = {
        upload: 'bulkuploadDealerSim',
        blank: 'bulkuploadBlankSim',
        mnp: 'bulkuploadMnpSim',
      }

      try {
        const params = createPostParams({
          action: bulkActions[this.bulkCategory],
          payload: { simpacks: payload },
        })
        const response = await this.$rest.post('postAction.php', params)

        const summary = (response.data && response.data.summary) || {}
        const issues = (response.data && response.data.issues) || []
        const results = (response.data && response.data.results) || []

        this.applyBulkRowResults(results, issues)

        const successCount = summary.successCount != null ? summary.successCount : payload.length
        this.snackbarText = `Uploaded ${successCount} of ${payload.length} SIM pack(s).` +
          (issues.length ? ` ${issues.length} issue(s) — see the Status column for details.` : '')
        if (issues.length) {
          this.snackbarError = true
        } else {
          this.snackbar = true
        }

        this.getDealerSim()
      } catch (error) {
        let message = error.message
        if (error.response) {
          message += ': ' + error.response.data
        }
        this.bulkTableData.forEach(row => {
          row.status = 'Error'
          row.remark = message
        })
        this.showBulkError(message)
      } finally {
        this.bulkSubmitting = false
      }
    },

    // Falls back to matching iccid inside `issues` text when the backend doesn't send per-row `results`.
    applyBulkRowResults (results, issues) {
      if (results.length) {
        const resultByIccid = new Map(results.map(result => [result.iccid, result]))
        this.bulkTableData.forEach(row => {
          const result = resultByIccid.get(row.iccid)
          if (!result) return
          const success = result.success === true || /^success$/i.test(result.status || '')
          row.status = success ? 'Success' : 'Error'
          row.remark = success ? null : (result.message || result.status || null)
        })
        return
      }

      this.bulkTableData.forEach(row => {
        const issue = issues.find(text => text.includes(row.iccid))
        row.status = issue ? 'Error' : 'Success'
        row.remark = issue || null
      })
    },
  },
}
</script>
